/**
 * EffectResolver.ts
 *
 * Resolves and applies powerup effects using dot notation targets.
 * Targets supported:
 * - player.health: Player health (add = heal, subtract = damage)
 * - player.biomatter: Player biomatter currency
 * - weapon.gun.*: Gun weapon ammo
 * - weapon.bomb.*: Bomb weapon ammo
 * - weapon.tractor.*: Tractor weapon ammo
 */

import Phaser from 'phaser';
import { Player } from '../entities/Player';
import { PowerUpEffect } from '../data/DataManagerPowerUp';

/**
 * Static class that resolves and applies powerup effects.
 */
export class EffectResolver {
  /**
   * Apply a single effect to the player
   * @param scene Current scene (for registry access)
   * @param player Player instance
   * @param effect Effect to apply
   * @returns true if effect was applied successfully
   */
  static apply(scene: Phaser.Scene, player: Player, effect: PowerUpEffect): boolean {
    const parts = effect.target.split('.');

    if (parts.length < 2) {
      console.warn(`[EffectResolver] Invalid effect target: ${effect.target}`);
      return false;
    }

    switch (parts[0]) {
      case 'player':
        return this.applyPlayerEffect(player, parts[1], effect);
      case 'weapon':
        if (parts.length < 3) {
          console.warn(`[EffectResolver] Weapon target requires slot and weaponId: ${effect.target}`);
          return false;
        }
        return this.applyWeaponEffect(player, parts[1], parts[2], effect);
      default:
        console.warn(`[EffectResolver] Unknown effect target category: ${parts[0]}`);
        return false;
    }
  }

  /**
   * Apply multiple effects to the player
   * @returns Number of effects successfully applied
   */
  static applyAll(scene: Phaser.Scene, player: Player, effects: PowerUpEffect[]): number {
    let successCount = 0;
    for (const effect of effects) {
      if (this.apply(scene, player, effect)) {
        successCount++;
      }
    }
    return successCount;
  }

  /**
   * Apply a player-related effect
   * Handles: player.health, player.biomatter
   */
  private static applyPlayerEffect(
    player: Player,
    property: string,
    effect: PowerUpEffect
  ): boolean {
    const value = effect.value;

    switch (property) {
      case 'health':
        if (effect.operation === 'add') {
          player.heal(value);
          console.log(`[EffectResolver] Healed player for ${value} HP`);
        } else {
          player.takeDamage(value);
          console.log(`[EffectResolver] Damaged player for ${value} HP`);
        }
        return true;

      case 'biomatter':
        if (player.weapons) {
          if (effect.operation === 'add') {
            player.weapons.addBiomatter(value);
            console.log(`[EffectResolver] Added ${value} biomatter`);
          } else {
            // Subtract biomatter (spend)
            const current = player.weapons.getBiomatter();
            player.weapons.addBiomatter(-Math.min(value, current));
            console.log(`[EffectResolver] Subtracted ${value} biomatter`);
          }
          return true;
        }
        console.warn('[EffectResolver] Player has no weapons manager for biomatter');
        return false;

      default:
        console.warn(`[EffectResolver] Unknown player property: ${property}`);
        return false;
    }
  }

  /**
   * Apply a weapon-related effect
   * Handles: weapon.gun.*, weapon.bomb.*, weapon.tractor.*
   */
  private static applyWeaponEffect(
    player: Player,
    slotType: string,
    weaponId: string,
    effect: PowerUpEffect
  ): boolean {
    if (!player.weapons) {
      console.warn('[EffectResolver] Player has no weapons manager');
      return false;
    }

    // Validate slot type
    if (!['gun', 'bomb', 'tractor'].includes(slotType)) {
      console.warn(`[EffectResolver] Invalid weapon slot type: ${slotType}`);
      return false;
    }

    const value = effect.value;
    const ammoToAdd = effect.operation === 'add' ? value : -value;

    // Use RuntimeManagerWeapon's addAmmo method
    const success = player.weapons.addAmmo(weaponId, ammoToAdd);

    if (success) {
      console.log(`[EffectResolver] ${effect.operation === 'add' ? 'Added' : 'Removed'} ${value} ammo for ${weaponId}`);
    } else {
      // Weapon might not be unlocked yet - try to unlock it first
      if (effect.operation === 'add') {
        const unlocked = player.weapons.unlockWeapon(weaponId, value);
        if (unlocked) {
          console.log(`[EffectResolver] Unlocked ${weaponId} with ${value} ammo`);
          return true;
        }
      }
      console.warn(`[EffectResolver] Failed to ${effect.operation} ammo for ${weaponId}`);
    }

    return success;
  }
}
